/**
 * @file useDispatchOrders.ts
 * @description Hook managing live delivery orders and courier fulfillment queue.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../config/supabase.config';
import { fetchDeliveryOrders, updateDeliveryStatus } from '../services/dispatch.service';
import type { DeliveryOrder, DeliveryOrderStatus } from '../../../types/dispatch.types';

export const useDispatchOrders = (venueId: string | null) => {
  const [orders, setOrders] = useState<DeliveryOrder[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadOrders = useCallback(async () => {
    if (!venueId) return;
    try {
      setError(null);
      const data = await fetchDeliveryOrders(venueId);
      setOrders(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load delivery orders');
    } finally {
      setIsLoading(false);
    }
  }, [venueId]);

  useEffect(() => {
    if (!venueId) return;
    loadOrders();

    const channelId = `dispatch-orders-${venueId}-${Math.random().toString(36).substring(7)}`;
    const channel = supabase
      .channel(channelId)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `venue_id=eq.${venueId}`,
        },
        () => {
          loadOrders();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [venueId, loadOrders]);

  const changeStatus = async (orderId: string, nextStatus: DeliveryOrderStatus) => {
    setOrders(prev => prev.map(o => (o.id === orderId ? { ...o, status: nextStatus } : o)));
    try {
      await updateDeliveryStatus(orderId, nextStatus);
    } catch (err) {
      loadOrders();
      throw err;
    }
  };

  const filteredOrders = filterStatus === 'all'
    ? orders
    : orders.filter(o => o.status === filterStatus);

  return {
    orders: filteredOrders,
    allOrders: orders,
    filterStatus,
    setFilterStatus,
    isLoading,
    error,
    changeStatus,
    refreshOrders: loadOrders,
  };
};
